// src/app/api/libraryApi.ts
import { baseApi } from "./baseApi";
import type { QuarterKey } from "./programmeApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type ContentType = "keynote" | "panel" | "reading" | "resource" | "task";

export interface ReadingItem {
  _id: string;
  title: string;
  author?: string;
  type: "book" | "article" | "video" | "podcast" | "other";
  description?: string;
  externalUrl?: string;
  isPrimary: boolean;
}

export interface ChapterMarker {
  timeSeconds: number;
  label: string;
}

export interface LibraryContent {
  id: string;
  contentType: ContentType;
  quarter: QuarterKey;
  cycle?: string;
  title: string;
  description?: string;
  durationMinutes?: number;
  durationLabel?: string; // "70 min" — computed by backend
  tags: string[];
  videoUrl?: string;
  thumbnailUrl?: string;
  fileUrl?: string;
  fileName?: string;
  fileSizeBytes?: number;
  fileType?: string;
  readingItems: ReadingItem[];
  chapterMarkers: ChapterMarker[];
  isNew: boolean;
  isPublished: boolean; // always returned; needed by admin for draft management
  isDownloadable: boolean;
  uploadedBy?: { firstName: string; lastName: string; role: string };
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface LibraryListResponse {
  success: boolean;
  message: string;
  count: number;
  data: LibraryContent[];
}

interface LibraryItemResponse {
  success: boolean;
  message: string;
  data: LibraryContent;
}

interface MessageResponse {
  success: boolean;
  message: string;
}

// Response from GET /api/library/:id/video-progress
export interface VideoProgressData {
  progress: number; // 0–1 fraction watched
  summary: string; // partial or complete summary text
  complete: boolean; // true if content_viewed record exists
}
interface VideoProgressResponse {
  success: boolean;
  data: VideoProgressData;
}
interface SaveProgressResponse {
  success: boolean;
  message: string;
  data: { progress: number; complete: boolean };
}

export interface LibraryFilters {
  quarter?: QuarterKey;
  type?: ContentType;
  search?: string;
  // Pass true from admin views to include draft (unpublished) items.
  // Only honoured by the backend when the caller has a content role.
  includeAll?: boolean;
}

interface CreateContentRequest {
  contentType: ContentType;
  quarter: QuarterKey;
  cycle?: string;
  title: string;
  description?: string;
  durationMinutes?: number;
  tags?: string[];
  videoUrl?: string;
  thumbnailUrl?: string;
  fileUrl?: string;
  fileName?: string;
  fileSizeBytes?: number;
  fileType?: string;
  readingItems?: Partial<ReadingItem>[];
  chapterMarkers?: ChapterMarker[];
  isDownloadable?: boolean;
  isPublished?: boolean;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const libraryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/library — full content list with optional filters
    getLibraryContent: builder.query<LibraryListResponse, LibraryFilters>({
      query: (params) => ({ url: "/library", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "LibraryContent" as const,
                id,
              })),
              { type: "LibraryContent", id: "LIST" },
            ]
          : [{ type: "LibraryContent", id: "LIST" }],
    }),

    // GET /api/library/:id — single content item
    getLibraryItem: builder.query<LibraryItemResponse, string>({
      query: (id) => `/library/${id}`,
      providesTags: (_result, _error, id) => [{ type: "LibraryContent", id }],
    }),

    // POST /api/library/:id/view — log that the mentee opened this item
    logView: builder.mutation<MessageResponse, string>({
      query: (id) => ({ url: `/library/${id}/view`, method: "POST" }),
    }),

    // POST /api/library/:id/download — log a download
    logDownload: builder.mutation<MessageResponse, string>({
      query: (id) => ({ url: `/library/${id}/download`, method: "POST" }),
    }),

    // POST /api/library — admin: upload a new content item
    createContent: builder.mutation<LibraryItemResponse, CreateContentRequest>({
      query: (body) => ({ url: "/library", method: "POST", body }),
      invalidatesTags: [{ type: "LibraryContent", id: "LIST" }],
    }),

    // PATCH /api/library/:id — admin: edit, publish, mark not-new
    updateContent: builder.mutation<
      LibraryItemResponse,
      { id: string } & Partial<CreateContentRequest & { isNew: boolean }>
    >({
      query: ({ id, ...body }) => ({
        url: `/library/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "LibraryContent", id },
        { type: "LibraryContent", id: "LIST" },
      ],
    }),

    // DELETE /api/library/:id — admin: remove content
    deleteContent: builder.mutation<MessageResponse, string>({
      query: (id) => ({ url: `/library/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "LibraryContent", id: "LIST" }],
    }),

    // ── Video progress — cross-device persistence ──────────────────────

    // GET /api/library/:id/video-progress
    // Fetches the server-side saved progress (0–1) + partial summary for
    // a video item.  Called when the VideoModal opens so progress from
    // another device seeds localStorage before playback starts.
    getVideoProgress: builder.query<VideoProgressResponse, string>({
      query: (id) => `/library/${id}/video-progress`,
      providesTags: (_r, _e, id) => [{ type: "VideoProgress" as const, id }],
    }),

    // PATCH /api/library/:id/video-progress
    // Saves mid-watch progress + optional summary to the backend.
    // Called on pause, modal close, and on final "Mark as watched".
    // When complete === true the backend also creates the content_viewed record.
    saveVideoProgress: builder.mutation<
      SaveProgressResponse,
      { id: string; progress: number; summary?: string; complete?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/library/${id}/video-progress`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id, complete }) => [
        { type: "VideoProgress" as const, id },
        // When marking as watched, also invalidate the activity + journey caches
        ...(complete
          ? [
              { type: "JourneyActivity" as const, id: "LIST" },
              { type: "JourneyQuarters" as const, id: "ALL" },
              { type: "JourneyProfile" as const, id: "CURRENT" },
            ]
          : []),
      ],
    }),
  }),
});

export const {
  useGetLibraryContentQuery,
  useGetLibraryItemQuery,
  useLogViewMutation,
  useLogDownloadMutation,
  useCreateContentMutation,
  useUpdateContentMutation,
  useDeleteContentMutation,
  useGetVideoProgressQuery,
  useSaveVideoProgressMutation,
} = libraryApi;
